import { Table } from '@/components/ui/';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useOpenOrders, useOrders } from '../../api/get-orders';
import { useOrdersStore } from '@/store/use-orders-store';
import { OrderType } from '@/types/enums';
import { useShallow } from 'zustand/react/shallow';
import Empty from '@/components/ui/empty';
import { useAccountStore } from '@/store/use-account-store';
import { useTranslation } from 'react-i18next';
import CancelOrder from '../cancel-order';
import CancelAllOrders from '../cancel-all-orders';
import MidPrice, { midPriceAccessorFn } from './mid-price';
import ReduceOnly, { reduceOnlyAccessorFn } from './reduce-only';
import Symbol, { symbolAccessorFn } from './symbol';
import OrderTypeComponent, { orderTypeAccessorFn } from './order-type';
import LimitPrice, { limitPriceAccessorFn } from './price';
import Value, { valueAccessorFn } from './value';
import TpSl from './tpsl';
import Time, { timeAccessorFn } from './time';
import { cn } from '@/utils/cn';

interface OpenOrdersTableProps {
  bgColor?: string;
  className?: string;
  noDataComponent?: React.ReactNode;
}

export default function OpenOrdersTable({
  bgColor,
  className,
  noDataComponent,
}: OpenOrdersTableProps) {
  const { t } = useTranslation();
  const { isLoading } = useOpenOrders();

  const address = useAccountStore((state) => state.address);

  const newOrders = useOrdersStore(
    useShallow((state) => {
      const { orders, newOrderIds } = state.getOrders(address);
      return Array.from(newOrderIds).filter(
        (id) => orders[id].orderType !== OrderType.MARKET,
      );
    }),
  );

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        id: 'time',
        accessorFn: timeAccessorFn,
        cell: ({ row }) => <Time orderId={row.original} />,
        header: t('openOrdersTable.time'),
        meta: { width: 'w-[14%] min-w-60' },
      },
      {
        accessorFn: orderTypeAccessorFn,
        cell: ({ row }) => <OrderTypeComponent orderId={row.original} />,
        header: t('openOrdersTable.type'),
        meta: { width: 'w-[10%] min-w-36' },
      },
      {
        accessorFn: symbolAccessorFn,
        header: t('openOrdersTable.market'),
        cell: ({ row }) => <Symbol orderId={row.original} />,
        meta: { width: 'w-[12%] min-w-52' },
      },
      // {
      //   accessorFn: sizeAccessorFn,
      //   cell: ({ row }) => <Size orderId={row.original} />,
      //   header: t('openOrdersTable.size'),
      //   sortDescFirst: false,
      //   meta: { width: 'w-[10%] min-w-44' },
      // },
      {
        accessorFn: valueAccessorFn,
        cell: ({ row }) => <Value orderId={row.original} />,
        header: t('openOrdersTable.value'),
        sortDescFirst: false,
        sortUndefined: 'last',
        meta: { width: 'w-[12%] min-w-44' },
      },
      {
        accessorFn: limitPriceAccessorFn,
        cell: ({ row }) => <LimitPrice orderId={row.original} />,
        header: t('openOrdersTable.limitPrice'),
        meta: { width: 'w-[12%] min-w-44' },
        sortDescFirst: false,
      },
      {
        accessorFn: midPriceAccessorFn,
        cell: ({ row }) => <MidPrice orderId={row.original} />,
        header: t('openOrdersTable.midPrice'),
        meta: { width: 'w-[12%] min-w-44' },
        sortDescFirst: false,
      },
      {
        id: 'tp-sl',
        cell: ({ row }) => <TpSl orderId={row.original} />,
        header: t('openOrdersTable.tpSl'),
        meta: { width: 'w-[16%] min-w-72' },
      },
      {
        accessorFn: reduceOnlyAccessorFn,
        cell: ({ row }) => <ReduceOnly orderId={row.original} />,
        header: t('openOrdersTable.reduceOnly'),
        meta: { width: 'w-[10%] min-w-40' },
      },
      {
        id: 'actions',
        header: CancelAllOrders,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <CancelOrder id={row.original} />
          </div>
        ),
        meta: {
          width: `w-[120px] min-w-[120px] sticky right-0 ${bgColor || 'bg-background'} [&>div>div]:ml-auto`,
        },
      },
    ],
    [t],
  );

  return (
    <Table
      id="open-orders"
      data={newOrders}
      columns={columns}
      loading={isLoading}
      initialSorting={[{ id: 'time', desc: true }]}
      isInfiniteScroll
      infiniteScrollOverscan={10}
      infiniteScrollEstimateRowSize={57}
      bgColor={bgColor}
      className={cn('[&_th]:min-h-16 [&_th]:py-[8.335px]', className)}
      rightGradientClassName="right-[120px]"
      emptyComponent={
        noDataComponent || (
          <Empty
            title={t('openOrdersTable.noOrdersFound')}
            description={t('openOrdersTable.noOrdersDescription')}
          />
        )
      }
    />
  );
}
